<script>
	import { api } from "../api";
	import { useFocus, useNavigate, Link } from "svelte-navigator";
	import RadioItem from "../components/RadioItem.svelte";
	import Select from "svelte-select";
	import { onDestroy, onMount } from "svelte";
	import { writable } from 'svelte/store'
	import { startDrawing, endDrawing, drawRectangle, fitBounds } from "../map";
	import { LatLng } from "leaflet";
	import notyf from "../utils/notyf";
	import AsyncButton from "../components/AsyncButton.svelte";
	import flightCapableDeviceFilter from '../utils/flightCapableDeviceFilter'
  import SrsMonitor from "../components/SRSMonitor.svelte";
	import formatNumber from "../utils/formatNumber"

	const devices = api.devices.store;
	const promise = Promise.all([api.devices.get()]);
	const navigate = useNavigate()

	const queryParams = new URLSearchParams(window.location.search);

	let flightName = queryParams.get("name") || "";
	let selectedARS = null
	let selectedGrs = {}
	let allSelectedGrs = new Set()
	let updateGRS = false

	const allItemsStore = writable(allSelectedGrs)

	const registerFocus = useFocus()

	function selectedARSChanged() {
		selectedGrs = {}
		allSelectedGrs.clear()

		if (selectedARS && selectedARS.radioType === 'LTE') {
			selectedGrs[selectedARS.id] = [{ id: selectedARS.id }]
			allSelectedGrs.add(selectedARS.id)
		}

		const attachedDevices = [selectedARS, ...Object.values($devices).filter(d => d.parentDeviceId === selectedARS.id)]
		for (const d of attachedDevices) {
			if (d.radioType !== 'ETHERNET') continue
			selectedGrs[d.id] = [{ id: d.id }]
			allSelectedGrs.add(d.id)
		}

		updateBounds()
	}

	function getAvailableGRS(devices, compDevice, _updatedGRS, _store) {
		return Object.values(devices).filter((d) => d.state == "STANDBY" && d.type === "GRS" && radioTypeCompare(d, compDevice)).sort((a, b) => a.name.localeCompare(b.name))
	}

	function selectionChanged(arsId) {
		return (event) => {
			updateGRS = arsId
			let items = event.detail
			if (!items) {
				for (const d of selectedGrs[arsId] ?? []) {
					allSelectedGrs.delete(d.id)
				}
				delete selectedGrs[arsId]
				allItemsStore.set(allSelectedGrs)
				updateBounds()
				return
			}

			if (!Array.isArray(items)) items = [items]

			let modified = false
			if (event.type == "select") {
				selectedGrs[arsId] = items
				for (const { id } of items) {
					if (!allSelectedGrs.has(id)) {
						allSelectedGrs.add(id)
						modified = true
					}
				}
			} else if (event.type == "clear") {
				modified = true
				delete selectedGrs[arsId]
				for (const { id } of items) {
					allSelectedGrs.delete(id)
				}
			}

			
			allItemsStore.set(allSelectedGrs)
			if (modified) updateBounds()
		}
	}

	function radioTypeCompare(a, b) {
		if (a.radioType === b.radioType) return true
		if ((a.radioType == "MICROSKYLINK" && b.radioType == "SKYLINK") || (b.radioType == "MICROSKYLINK" && a.radioType == "SKYLINK")) return true
		return false
	}

	let rect = null;
	function updateBounds() {
		const RADIO_DISTANCE_IN_METERS = 30 * 1609.344; // 30 miles in meters
		if (rect) {
			rect.remove();
			rect = null;
		}

		let selectedDevices = [...allSelectedGrs].map(id => $devices[id])

		if (!selectedDevices.length) return;
		let bounds;
		for (const device of selectedDevices) {
			if (device.latitude === 0 && device.longitude === 0) continue

			const extents = new LatLng(device.latitude, device.longitude).toBounds(
				RADIO_DISTANCE_IN_METERS
			);
			if (!bounds) {
				bounds = extents;
			} else {
				bounds.extend(extents);
			}
		}
		if (!bounds) return
		fitBounds(bounds);
		rect = drawRectangle(bounds, {
			color: "#ff7800",
			weight: 2,
			fill: 0.1,
			dashArray: "4 10",
			draggable: true,
		});
	}

	async function save() {
		if (!selectedARS) {
			notyf.error("ARS is required");
			return;
		}
		if (!allSelectedGrs.size) {
			notyf.error("At least one GRS is required");
			return;
		}
		try {
			let bounds;
			if (rect) {
				bounds = {
					north: rect._bounds.getNorth(),
					south: rect._bounds.getSouth(),
					east: rect._bounds.getEast(),
					west: rect._bounds.getWest(),
				};
			}
			let flightLinks = []
			for (const arsId of Object.keys(selectedGrs)) {
				const linkGrses = selectedGrs[arsId]
				if (linkGrses && linkGrses.length) {
					const linkInfo = {
						arsDeviceId: arsId,
						grsDeviceIds: linkGrses.map(i => i.id),
					};
					flightLinks.push(linkInfo)
				}
			}
			if (flightLinks.length <= 0) {
				notyf.error("Attempted to create a fligth with zero links.  Check your ARS and GRS configurations.")
				return
			}
			let flightInfo = {
				name: flightName,
				arsDeviceId: selectedARS.id,
				bounds,
				links: flightLinks
			}
			flightInfo = await api.flights.create(flightInfo)
			
			notyf.success('Created Flight')
			navigate('/flights/' + flightInfo.id)
		} catch (error) {
			notyf.error(error.message);
		}
	}

	function LTEReady(device, devices) {
		return devices[device.id].state === "STANDBY"
	}

	function getRadioSelectionItems(devices, ars, store, _updated) {
		const values =  Object.values(devices).
			filter((d) => d.state == "STANDBY" && d.type === "GRS" && radioTypeCompare(d, ars) && !allSelectedGrs.has(d.id)).
			sort((a, b) => a.name.localeCompare(b.name)).
			map(d => {return { id: d.id, name: d.name }})
		return values
	}

	onMount(startDrawing);
	onDestroy(endDrawing);

	function isFrequencyValid(frequency) {
		return frequency && frequency > 5030000000n && frequency <= 5090000000n
	}
</script>

<svelte:head>
	<title>SkyLine - New Flight</title>
</svelte:head>

{#await promise then _}
	<div class="sidebar">
		<div class="sidebar-tile">
			<input
				type="text"
				placeholder="Flight Name"
				use:registerFocus
				bind:value={flightName}
			/>
			<h3>ARS Device</h3>
			<div class="themed acPicker">
				<Select
					items={flightCapableDeviceFilter(Object.values($devices)).sort((a,b) => a.name.localeCompare(b.name)).map(d => { return {id:d.id, name:d.name, radioType:d.radioType, frequency:d.frequency}})}
					optionIdentifier="id"
					labelIdentifier="name"
					placeholder="Select the ARS"
					isClearable={false}
					showIndicator={true}
					listOffset={0}
					bind:value={selectedARS}
					on:select={selectedARSChanged}
				/>
			</div>
			{#if selectedARS}
				{#if (selectedARS.radioType === "SKYLINK" || selectedARS.radioType === "MICROSKYLINK") && !isFrequencyValid(selectedARS.frequency)}
					<div class='warning'>Your ARS does not have a frequency assigned and must be <Link to='/devices/{selectedARS.id}'>updated</Link> before use.</div>
				{:else if (selectedARS.radioType === "SKYLINK" || selectedARS.radioType === "MICROSKYLINK")}
					<div class="frequency">ARS Frequency: {formatNumber(selectedARS.frequency, 0)}</div>
				{/if}
				{#if selectedARS.radioType !== 'LTE' && selectedARS.radioType !== 'ETHERNET'}
					<h3>Ground Radios</h3>
					<div class="themed radio-picker">
						<Select
							items={getAvailableGRS($devices, $devices[selectedARS.id], updateGRS, $allItemsStore)}
							optionIdentifier="id"
							labelIdentifier="name"
							placeholder="Select the GRS"
							showIndicator={true}
							isMulti={true}
							Item={RadioItem}
							listOffset={0}
							
							on:select={selectionChanged(selectedARS.id)}
							on:clear={selectionChanged(selectedARS.id)}
						/>
					</div>
				{:else}
					{@const attachedDevices = [selectedARS, ...Object.values($devices).filter(d => d.parentDeviceId === selectedARS.id)]}
					{#each attachedDevices as ars}
						{#if ars.radioType === "LTE" || ars.radioType === 'ETHERNET'}
							<h3>{ars.name} {ars.radioType} Connection</h3>
							{#if !LTEReady(ars, $devices)}
								<div class='warning'>Network Not Ready</div>
							{:else}
								<div class="lteReady">
                  Network Ready
                  {#if ars.latitude === 0 || ars.longitude === 0}
                    <span class='warning'>GPS Not Ready</span>
                  {/if}
                </div>
							{/if}
						{:else}
							<h3>Ground Radios for {ars.name}</h3>
							{#if (ars.radioType === "SKYLINK" || ars.radioType === "MICROSKYLINK") && !isFrequencyValid(ars.frequency)}
								<div class='warning'>Your ARS does not have a frequency assigned and must be <Link to='/devices/{ars.id}'>updated</Link> before use.</div>
							{:else}
								{#if (ars.radioType === "SKYLINK" || ars.radioType === "MICROSKYLINK")}
									<div class="frequency">ARS Frequency: {formatNumber(ars.frequency, 0)}</div>
								{/if}
							
								<div class="themed radio-picker">
									<Select
										items={getRadioSelectionItems($devices, ars, $allItemsStore, updateGRS).map(d=>{ return {id:d.id, name:d.name}})}
										optionIdentifier="id"
										labelIdentifier="name"
										placeholder="Select the GRS"
										showIndicator={true}
										isMulti={true}
										Item={RadioItem}
										listOffset={0}
										
										on:select={selectionChanged(ars.id)}
										on:clear={selectionChanged(ars.id)}
									/>
								</div>
							{/if}
							
						{/if}
					{/each}
				{/if}
			{/if}
			<div class="buttons">	
				<AsyncButton color="green" handler={save}>Create Flight</AsyncButton>
				<Link class="cancel" to="/flights">Cancel Flight</Link>
			</div>
		</div>
	</div>
{/await}

<SrsMonitor />

<style>
	.themed {
		color:white;
		--background: black;
		--listBackground: lightgray;
		--listColor: black;
		--itemColor: black;
		--itemHoverBG: rgb(180, 180, 180);
		--itemHoverColor: black;
		--multiItemBG: rgb(49, 49, 49);
		--listBorderRadius: 0 0 8px 8px;
		--itemFirstBorderRadius: 0;
		--inputColor: #fff;
		--itemIsActiveBG: white;
		--itemIsActiveColor: black;
		--listShadow:0 4px 4px 2px rgba(0, 0, 0, 0.54)
	}

	.frequency {
		color: white;
		margin: 0 0 4px 8px;
	}

	h3:not(:first-child) {
		margin-top:8px;
	}

	.themed.radio-picker :global(.listContainer) {
		padding-bottom:8px;
	}

	.acPicker {
		margin-bottom: 16px;
	}

	.acPicker :global(.listItem) {
		border-bottom: 1px solid #e3e3e3;
	}

	input {
		font-size: 14pt;
    border-bottom: 1px solid white;
    background-color: #333;
	}

	input::placeholder {
		color:darkgrey;
	}

	.sidebar {
		background-color:rgb(37 37 37);
	}

	.buttons {
		display: grid;
		gap: 8px;
	}

	.warning {
		color:orange;
		font-style: italic;
		margin: 16px;
		font-weight: bold;
	}

	.lteReady {
    margin: 8px;
    margin-left: 0;
    color: #13d58a;
    text-transform: uppercase;
	}
</style>
